import { useCallback } from 'react';
import type { LatLng } from '~/_shared/types/latLng';
import { convertColorToWebGLColor } from '~/_shared/utils/colors/colors.helpers';
import { MapCircleComponent } from '../mapObjects/mapCircle/mapCircle.component';
import type { MapCircleInstance } from '../mapObjects/mapCircle/mapCircleModel';
import { MapMarkerComponent } from '../mapObjects/mapMarker/mapMarker.component';
import { PredefinedTemplate } from '../markers/manager/mapMarkerTemplates';
import {
  proximityCenterMarkerDefaultColor, proximityCenterMarkerSize,
} from '../proximity/mapProximityManager';

export type ShapeDrawRenderCircleProps = Readonly<{
  borderColor: string;
  borderOpacity: number;
  borderWidth: number;
  fillColor: string;
  fillOpacity: number;
  withCenterMarker?: boolean;
  units: CircleAreaUnit;
}>;

export const useShapeDrawCircleProps = (params: Readonly<ShapeDrawRenderCircleProps>) => {
  const { borderColor, borderOpacity, borderWidth, fillColor, fillOpacity, withCenterMarker, units } = params;
  const fillColorWebGL = convertColorToWebGLColor(fillColor, fillOpacity / 100);
  const borderColorWebGL = convertColorToWebGLColor(borderColor, borderOpacity / 100);

  const renderCircle = useCallback((id: string, circle: MapCircleInstance) => (
    <MapCircleComponent
      key={id}
      circle={circle}
      visuals={{
        circleArea: {
          fillColor: fillColorWebGL,
          borderWidth,
          borderColor: borderColorWebGL,
        },
      }}
    />
  ), [borderColorWebGL, borderWidth, fillColorWebGL]);

  const renderCenter = useCallback((key: string, center: LatLng) => (
    <MapMarkerComponent
      key={key}
      marker={{
        id: key,
        lat: center.lat,
        lng: center.lng,
        anchor: 'center',
        size: CENTER_MARKER_DEFAULT_PROPS.size,
      }}
      visuals={{
        template: CENTER_MARKER_DEFAULT_PROPS.template,
        color: CENTER_MARKER_DEFAULT_PROPS.color,
        interactive: false,
      }}
    />
  ), []);

  return {
    renderCircle,
    renderCenter: withCenterMarker ? renderCenter : undefined,
    units,
  };
};

const CENTER_MARKER_DEFAULT_PROPS = {
  color: convertColorToWebGLColor(proximityCenterMarkerDefaultColor),
  interactive: false,
  size: proximityCenterMarkerSize,
  template: PredefinedTemplate.ProximityCenter,
} as const;
